import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Check, Copy, Download, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  createCaptureToken,
  listCaptureTokens,
  quickSaveUrl,
  revokeCaptureToken,
  type CaptureTokenRow,
} from '@/lib/captureTokens';
import {
  SHORTCUT_FILE_PATH,
  canInstallShortcuts,
  copyPendingText,
  isShortcutFileAvailable,
} from '@/lib/shortcutInstall';
import { isStandalone } from '@/lib/appInstall';

interface QuickSaveSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CopyField({ label, value, secret = false }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed. Select the text and copy it by hand.');
    }
  };
  return (
    <div className="space-y-1">
      <p className="text-xs text-ink-3">{label}</p>
      <div className="flex items-center gap-2 rounded-lg border border-line bg-paper-sunken px-2.5 py-2">
        <code className={`flex-1 min-w-0 break-all text-[12px] text-ink ${secret ? 'select-all' : ''}`}>{value}</code>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={copy} aria-label={`Copy ${label}`}>
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

/** The desktop dialog (avatar menu > Save from iPhone). Phones show QuickSaveSetup in the account sheet instead. */
export function QuickSaveSettings({ open, onOpenChange }: QuickSaveSettingsProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Save from iPhone
          </DialogTitle>
        </DialogHeader>
        <QuickSaveSetup active={open} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Capture tokens for the iOS Shortcut: one-tap install where the signed file
 * is served, or a token (shown once) and URL to build it by hand; then see
 * when each token was used, and revoke. `active` is whether it's on screen.
 */
export function QuickSaveSetup({ active }: { active: boolean }) {
  const [tokens, setTokens] = useState<CaptureTokenRow[] | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oneTap, setOneTap] = useState(false);
  // From the home screen, a download can't open Shortcuts in place, so the
  // file opens in Safari through a plain link the person taps
  const [openInSafari, setOpenInSafari] = useState(false);

  const load = useCallback(async () => {
    try {
      setTokens(await listCaptureTokens());
    } catch {
      setTokens([]);
      toast.error("Couldn't load your capture tokens");
    }
  }, []);

  useEffect(() => {
    if (active) load();
    else {
      setNewToken(null);
      setOpenInSafari(false);
    }
  }, [active, load]);

  useEffect(() => {
    if (!active || !canInstallShortcuts()) return;
    let cancelled = false;
    isShortcutFileAvailable().then((ok) => {
      if (!cancelled) setOneTap(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  /**
   * Makes a token (or reuses the one on screen), copies it, then opens the
   * signed Shortcut. Shortcuts asks for the token on import, so it's already
   * on the clipboard when the question comes up.
   */
  const addToIPhone = async () => {
    setBusy(true);
    const token = newToken ? Promise.resolve(newToken) : createCaptureToken();
    let copied = true;
    try {
      await copyPendingText(token);
    } catch {
      copied = false;
    }
    let value: string;
    try {
      value = await token;
    } catch {
      toast.error("Couldn't create a token. Try again.");
      setBusy(false);
      return;
    }
    if (value !== newToken) {
      setNewToken(value);
      load();
    }
    setBusy(false);
    if (!copied) {
      toast.error('Copy the token below, then tap Add to iPhone again.');
      return;
    }
    if (isStandalone()) {
      setOpenInSafari(true);
      toast.success('Token copied. Now tap Open in Safari.');
      return;
    }
    toast.success('Token copied. Paste it when Shortcuts asks for it.');
    window.location.assign(SHORTCUT_FILE_PATH);
  };

  const create = async () => {
    setBusy(true);
    try {
      setNewToken(await createCaptureToken());
      await load();
    } catch {
      toast.error("Couldn't create a token. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await revokeCaptureToken(id);
      setTokens((t) => t?.filter((row) => row.id !== id) ?? null);
      toast.success('Token revoked. Shortcuts using it stop working now.');
    } catch {
      toast.error("Couldn't revoke that token");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-2 -mt-1">
        Share links, screenshots and text from any app straight to your Home board with an iOS Shortcut.
        {oneTap
          ? ' Tap Add to iPhone: it copies a new token and opens the Shortcut. Paste the token when Shortcuts asks.'
          : ' Make a token, then paste it and the URL below into the Shortcut.'}
      </p>

      {oneTap &&
        (openInSafari ? (
          <Button asChild className="w-full">
            <a href={SHORTCUT_FILE_PATH} target="_blank" rel="noopener noreferrer">
              <Download className="mr-2 h-4 w-4" />
              Open in Safari
            </a>
          </Button>
        ) : (
          <Button onClick={addToIPhone} disabled={busy} className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Add to iPhone
          </Button>
        ))}

      {oneTap && <p className="text-xs font-medium text-ink-2 pt-1">Or build it by hand</p>}

      <CopyField label="Shortcut URL" value={quickSaveUrl()} />

      {newToken ? (
        <div className="space-y-2 rounded-xl border border-line bg-paper-raised p-3">
          <CopyField label="Your new token (shown once)" value={newToken} secret />
          <p className="text-xs text-ink-3">
            Copy it now. Mindfull only keeps a fingerprint of it, so you can't see it again. Anyone with this token can
            add things to your Mindfull, so keep it in the Shortcut only.
          </p>
        </div>
      ) : (
        <Button onClick={create} disabled={busy} variant={oneTap ? 'outline' : 'default'} className="w-full">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create a token
        </Button>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-ink-2">Your tokens</p>
        {tokens === null ? (
          <Loader2 className="h-4 w-4 animate-spin text-ink-3" />
        ) : tokens.length === 0 ? (
          <p className="text-xs text-ink-3">None yet.</p>
        ) : (
          tokens.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-ink truncate">
                  {t.name} <span className="font-mono text-xs text-ink-3">{t.token_prefix}…</span>
                </p>
                <p className="text-xs text-ink-3">
                  {t.last_used_at
                    ? `Last used ${formatDistanceToNow(new Date(t.last_used_at), { addSuffix: true })}`
                    : `Created ${formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}, not used yet`}
                </p>
              </div>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => revoke(t.id)}>
                Revoke
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
